import { FC } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import CarCrash from '@mui/icons-material/CarCrash';

/**
 * Props for the MileageErrorPanel component.
 */
interface MileageErrorPanelProps {
	/** The annual mileage limit to display when actual usage cannot be retrieved. */
	annualMileage: number;
}

/**
 * Displays a friendly error state when mileage data cannot be retrieved.
 *
 * Shown as a fallback when the telematics API fails to return mileage data.
 * Displays a car icon, explanatory message, and the known annual mileage limit
 * so users can still proceed with mileage top-up if needed.
 *
 * @component
 * @example
 * ```tsx
 * // Show error panel with known annual limit
 * <MileageErrorPanel annualMileage={10000} />
 * ```
 */
const MileageErrorPanel: FC<MileageErrorPanelProps> = ({ annualMileage }) => {
	const theme = useTheme();

	return (
		<Paper
			elevation={0}
			sx={{
				px: 3,
				pt: 2,
				borderRadius: 2,
				textAlign: 'center',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					mb: 1,
				}}
			>
				<CarCrash
					sx={{
						fontSize: 48,
						color: theme.palette.primary.main,
						opacity: 0.9,
					}}
				/>
			</Box>

			<Typography
				variant='h6'
				sx={{
					mb: 1.5,
					color: theme.palette.text.primary,
					fontWeight: 500,
				}}
			>
				Unable to retrieve your mileage data
			</Typography>

			<Typography
				variant='body2'
				sx={{
					mb: 2.5,
					color: theme.palette.text.secondary,
					maxWidth: '80%',
					mx: 'auto',
				}}
			>
				We&apos;re experiencing a temporary issue accessing your mileage information. You can still proceed with topping
				up your mileage.
			</Typography>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					backgroundColor: theme.palette.background.paper,
					borderRadius: 1,
					p: 1.5,
				}}
			>
				<Typography
					variant='body2'
					sx={{
						fontWeight: 500,
						color: theme.palette.text.secondary,
					}}
				>
					Annual Limit:
				</Typography>
				<Typography
					variant='body1'
					sx={{
						fontWeight: 600,
						color: theme.palette.primary.main,
					}}
				>
					{annualMileage.toLocaleString()} miles
				</Typography>
			</Box>
		</Paper>
	);
};

export default MileageErrorPanel;
